<template>
  <div class="balance-transfer-calculator">
    <h1 class="btc-title">Balance Transfer Calculator</h1>
    <p class="btc-intro">Ready to start your interest-free era? Our balance transfer calculator can help you see just how close you are to being debt-free. Enter a few quick details about your current card and we'll estimate how much faster you can move forward.</p>

    <div class="btc-container">
      <div class="btc-left">
        <h2 class="btc-section-title">Your Current Credit Card Details:</h2>
        <form @submit.prevent role="form" aria-describedby="calcDescription">
          <div class="btc-field">
            <label for="balance" class="btc-label">Your Current Balance</label>
            <div class="btc-input-with-prefix">
              <span class="btc-prefix" aria-hidden="true">$</span>
              <input id="balance" name="balance" class="btc-input" :class="{ 'invalid': balanceInvalid }" inputmode="decimal" type="text" :value="formatNumberInput(balance)" @input="onBalanceInput" placeholder="XX,XXX" aria-describedby="balance-error" />
            </div>
          </div>

          <div class="btc-field">
            <label for="currentApr" class="btc-label">Your Current Interest Rate</label>
            <div class="btc-input-with-suffix">
              <input id="currentApr" name="currentApr" class="btc-input" :class="{ 'invalid': currentAprInvalid }" type="text" v-model="currentApr" @input="onInput" placeholder="XX" aria-describedby="currentApr-suffix currentApr-error" />
              <span class="btc-suffix" id="currentApr-suffix" aria-hidden="true">%</span>
            </div>
          </div>

          <div class="btc-field">
            <label for="monthlyPayment" class="btc-label">Your Current Monthly Payment</label>
            <div class="btc-input-with-prefix">
              <span class="btc-prefix" aria-hidden="true">$</span>
              <input id="monthlyPayment" name="monthlyPayment" class="btc-input" :class="{ 'invalid': monthlyPaymentInvalid }" type="text" :value="formatNumberInput(monthlyPayment)" @input="onMonthlyPaymentInput" placeholder="XXX" aria-describedby="monthlyPayment-error" />
            </div>
          </div>

          <h2 class="btc-section-title">Ardent Platinum Credit Card Details:</h2>

          <div class="btc-field">
            <label for="promoMonths" class="btc-label">0% Intro APR Length</label>
            <div class="btc-input-with-suffix">
              <input id="promoMonths" name="promoMonths" class="btc-input" :class="{ 'invalid': promoMonthsInvalid, 'valid': promoMonthsValid }" type="text" v-model="promoMonths" @input="onInput" placeholder="12" aria-describedby="promoMonths-suffix promoMonths-error" />
              <span class="btc-suffix" id="promoMonths-suffix" aria-hidden="true">Billing Cycles</span>
            </div>
          </div>

          <div class="btc-field">
            <label for="postPromoApr" class="btc-label">APR After Intro</label>
            <div class="btc-input-with-suffix">
              <input id="postPromoApr" name="postPromoApr" class="btc-input" :class="{ 'invalid': postPromoAprInvalid, 'valid': postPromoAprValid }" type="text" v-model="postPromoApr" @input="onInput" placeholder="12.99" aria-describedby="postPromoApr-suffix postPromoApr-error" />
              <span class="btc-suffix" id="postPromoApr-suffix" aria-hidden="true">%</span>
            </div>
          </div>

          <div class="btc-field">
            <label for="transferFeePct" class="btc-label">Balance Transfer Fee</label>
            <div class="btc-input-with-suffix">
              <input id="transferFeePct" name="transferFeePct" class="btc-input" :class="{ 'invalid': transferFeePctInvalid, 'valid': transferFeePctValid }" type="text" v-model="transferFeePct" @input="onInput" placeholder="2.5" aria-describedby="transferFeePct-suffix transferFeePct-error" />
              <span class="btc-suffix" id="transferFeePct-suffix" aria-hidden="true">%</span>
            </div>
          </div>

          <div class="btc-actions">
            <button type="button" class="btc-button" @click="recalculate" :disabled="anyFieldInvalid">CALCULATE</button>
          </div>

          <p id="calcDescription" class="btc-help-text">Values update automatically as you type. Press Calculate to re-announce results for screen readers.</p>
        </form>
      </div>

      <div class="btc-right">
        <div class="btc-results" aria-live="polite" aria-atomic="true">
          <!-- All field errors grouped above the results in the white box -->
          <div v-if="anyFieldInvalid" class="btc-error" role="alert" aria-live="assertive">
            <ul style="margin:0; padding-left:1.2em;">
              <li v-if="balanceInvalid">Please enter a valid balance greater than $0</li>
              <li v-if="currentAprInvalid">Please enter a valid interest rate (0-100%)</li>
              <li v-if="monthlyPaymentInvalid">Please enter a valid monthly payment greater than $0</li>
              <li v-if="promoMonthsInvalid">Please enter a valid number of billing cycles</li>
              <li v-if="postPromoAprInvalid">Please enter a valid APR (0-100%)</li>
              <li v-if="transferFeePctInvalid">Please enter a valid fee (0-100%)</li>
            </ul>
          </div>
          <div v-else-if="hasPaymentError" class="btc-error" role="alert" aria-live="assertive">Monthly payment is too low to reduce the balance (must exceed monthly interest charge).</div>

          <h3 class="btc-results-title">Estimated Savings:</h3>
          <p class="btc-savings" v-if="!hasPaymentError">{{ formatCurrencyDisplay(estimatedSavings) }}</p>
          <p class="btc-savings" v-else>$X,XXX</p>

          <p class="btc-payoff-text">Using the Ardent Platinum Credit Card, you will pay off your debt in:</p>
          <p class="btc-months" v-if="!hasPaymentError">{{ totalPayoffMonthsRounded }} Billing Cycles</p>
          <p class="btc-months" v-else>XX Billing Cycles</p>

          <p class="btc-breakdown-title">Estimated Interest & Fees with Your Current Card:</p>
          <p class="btc-breakdown-value" v-if="!hasPaymentError">{{ formatCurrencyDisplay(interestCurrentApprox) }}</p>
          <p class="btc-breakdown-value" v-else>$X,XXX</p>

          <p class="btc-breakdown-title">Estimated Interest & Fees with your Ardent Platinum Credit Card:</p>
          <p class="btc-breakdown-value" v-if="!hasPaymentError">{{ formatCurrencyDisplay(interestArdentApprox + transferFeeAmount) }}</p>
          <p class="btc-breakdown-value" v-else>$X,XXX</p>

          <a href="https://app.loanspq.com/cc/CreditCard.aspx?enc=Kw21Wblm1yxpjJabdoZaDxROpm0w4fnwOz-cSsy3nYujbifF1TqMToLBYhOhXODFKIPfnHyiKnVk4YzSwDkyCd3lWCU1m_qhVjWak_dJHdDmtY2GjdbXYsl_7q6U10tWC7gpTOBC0zeGiEPD2nMFwPFNU7D2Z3X3PTtqcicc7V8zGrcbFO2R4rtwxTGQ9bdx" class="btc-apply-button">APPLY NOW</a>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  name: 'BalanceTransferCalculator',
  props: {
    // Allow overriding defaults via data attributes or server-side binding later if needed
    initialBalance: { type: Number, default: 5000 },
    initialTransferFeePct: { type: Number, default: 2.5 },
    initialCurrentApr: { type: Number, default: 24 },
    initialPromoApr: { type: Number, default: 0 },
    initialPostPromoApr: { type: Number, default: 12.99 },
    initialPromoMonths: { type: Number, default: 12 },
    initialMonthlyPayment: { type: Number, default: 250 }
  },
  data() {
    return {
      balance: this.initialBalance,
      transferFeePct: this.initialTransferFeePct,
      currentApr: this.initialCurrentApr,
      promoApr: this.initialPromoApr,
      postPromoApr: this.initialPostPromoApr,
      promoMonths: this.initialPromoMonths,
      monthlyPayment: this.initialMonthlyPayment,
      announceToggle: false // used to retrigger aria-live
    }
  },
  computed: {
    anyFieldInvalid() {
      return this.balanceInvalid || this.currentAprInvalid || this.monthlyPaymentInvalid || this.transferFeePctInvalid || this.postPromoAprInvalid || this.promoMonthsInvalid;
    },
    // Parse string inputs as numbers
    balanceNum() { return parseFloat(String(this.balance).replace(/[^0-9.]/g, '')) || 0 },
    currentAprNum() { return parseFloat(String(this.currentApr).replace(/[^0-9.]/g, '')) || 0 },
    monthlyPaymentNum() { return parseFloat(String(this.monthlyPayment).replace(/[^0-9.]/g, '')) || 0 },
    transferFeePctNum() { return parseFloat(String(this.transferFeePct).replace(/[^0-9.]/g, '')) || 0 },
    postPromoAprNum() { return parseFloat(String(this.postPromoApr).replace(/[^0-9.]/g, '')) || 0 },
    promoMonthsNum() { return parseInt(String(this.promoMonths).replace(/[^0-9]/g, '')) || 0 },
    promoAprNum() { return parseFloat(String(this.promoApr).replace(/[^0-9.]/g, '')) || 0 },

    // Monthly rates as decimals
    rCurrent() { return (this.currentAprNum / 100) / 12 },
    rPromo() { return (this.promoAprNum / 100) / 12 },
    rPost() { return (this.postPromoAprNum / 100) / 12 },
    payoffMonthsCurrent() {
      if (this.monthlyPaymentNum <= this.balanceNum * this.rCurrent) return NaN;
      const numerator = Math.log(this.monthlyPaymentNum / (this.monthlyPaymentNum - this.balanceNum * this.rCurrent));
      const denominator = Math.log(1 + this.rCurrent);
      return numerator / denominator;
    },
    remainingAfterPromo() {
      // Remaining balance after promo period payments
      const n = this.promoMonthsNum;
      const P = this.monthlyPaymentNum;
      const B = this.balanceNum;
      const r = this.rPromo;
      if (n === 0) return B; // no promo period
      let remaining;
      if (r === 0) {
        remaining = B - P * n;
      } else {
        const growth = Math.pow(1 + r, n);
        remaining = B * growth - P * ((growth - 1) / r);
      }
      return remaining < 0 ? 0 : remaining;
    },
    postPromoMonths() {
      const R = this.remainingAfterPromo;
      if (R <= 0) return 0;
      if (this.monthlyPaymentNum <= R * this.rPost) return NaN;
      const numerator = Math.log(this.monthlyPaymentNum / (this.monthlyPaymentNum - R * this.rPost));
      const denominator = Math.log(1 + this.rPost);
      return numerator / denominator;
    },
    totalPayoffMonths() {
      if (this.hasPaymentError) return NaN;
      return this.promoMonthsNum + this.postPromoMonths;
    },
    // Approximate interest costs (simple approximation per requirements)
    interestCurrentApprox() {
      if (this.hasPaymentError) return 0;
      return (this.monthlyPaymentNum * this.payoffMonthsCurrent) - this.balanceNum;
    },
    interestPromoApprox() { // interest during promo period
      return this.balanceNum * this.rPromo * this.promoMonthsNum; // promoApr may be 0
    },
    interestPostApprox() { // interest during post-promo period
      return this.remainingAfterPromo * this.rPost * this.postPromoMonths;
    },
    interestArdentApprox() {
      if (this.hasPaymentError) return 0;
      return this.interestPromoApprox + this.interestPostApprox;
    },
    transferFeeAmount() { return this.balanceNum * (this.transferFeePctNum / 100); },
    estimatedSavings() {
      return this.interestCurrentApprox - (this.interestArdentApprox + this.transferFeeAmount);
    },
    hasPaymentError() {
      return this.monthlyPaymentNum <= this.balanceNum * this.rCurrent || (this.remainingAfterPromo > 0 && this.monthlyPaymentNum <= this.remainingAfterPromo * this.rPost);
    },
    payoffMonthsCurrentRounded() { return this.roundDisplay(this.payoffMonthsCurrent); },
    postPromoMonthsRounded() { return this.roundDisplay(this.postPromoMonths); },
    totalPayoffMonthsRounded() { return this.roundDisplay(this.totalPayoffMonths); },

    // Field validation - check if input contains non-numeric characters or results in invalid number
    balanceInvalid() {
      const str = String(this.balance).trim();
      if (str === '') return false;
      // Check if contains letters or invalid chars (allow only digits, dots, commas, dollar signs)
      if (/[a-zA-Z!@#%^&*()_+=\[\]{};':"\\|<>\/?]/.test(str)) return true;
      return this.balanceNum <= 0 || isNaN(this.balanceNum);
    },
    currentAprInvalid() {
      const str = String(this.currentApr).trim();
      if (str === '') return false;
      if (/[a-zA-Z!@#$%^&*()_+=\[\]{};':"\\|<>\/?]/.test(str)) return true;
      return this.currentAprNum <= 0 || this.currentAprNum > 100 || isNaN(this.currentAprNum);
    },
    monthlyPaymentInvalid() {
      const str = String(this.monthlyPayment).trim();
      if (str === '') return false;
      if (/[a-zA-Z!@#%^&*()_+=\[\]{};':"\\|<>\/?]/.test(str)) return true;
      return this.monthlyPaymentNum <= 0 || isNaN(this.monthlyPaymentNum);
    },
    transferFeePctInvalid() {
      const str = String(this.transferFeePct).trim();
      if (str === '') return false;
      if (/[a-zA-Z!@#$%^&*()_+=\[\]{};':"\\|<>\/?]/.test(str)) return true;
      return this.transferFeePctNum < 0 || this.transferFeePctNum > 100 || isNaN(this.transferFeePctNum);
    },
    postPromoAprInvalid() {
      const str = String(this.postPromoApr).trim();
      if (str === '') return false;
      if (/[a-zA-Z!@#$%^&*()_+=\[\]{};':"\\|<>\/?]/.test(str)) return true;
      return this.postPromoAprNum < 0 || this.postPromoAprNum > 100 || isNaN(this.postPromoAprNum);
    },
    promoMonthsInvalid() {
      const str = String(this.promoMonths).trim();
      if (str === '') return false;
      if (/[a-zA-Z!@#$%^&*()_+=\[\]{};':"\\|<>\/?]/.test(str)) return true;
      return this.promoMonthsNum <= 0 || isNaN(this.promoMonthsNum);
    },

    balanceValid() { return this.balance !== '' && !this.balanceInvalid; },
    currentAprValid() { return this.currentApr !== '' && !this.currentAprInvalid; },
    monthlyPaymentValid() { return this.monthlyPayment !== '' && !this.monthlyPaymentInvalid; },
    transferFeePctValid() { return this.transferFeePct !== '' && !this.transferFeePctInvalid; },
    postPromoAprValid() { return this.postPromoApr !== '' && !this.postPromoAprInvalid; },
    promoMonthsValid() { return this.promoMonths !== '' && !this.promoMonthsInvalid; }
  },
  methods: {
    roundDisplay(val) {
      if (isNaN(val) || !isFinite(val)) return '—';
      return (Math.round(val * 10) / 10).toString();
    },
    formatCurrency(val) {
      return new Intl.NumberFormat('en-US', { 
        style: 'currency', 
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(val);
    },
    formatCurrencyDisplay(val) {
      return new Intl.NumberFormat('en-US', { 
        style: 'currency', 
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(val);
    },
    formatNumberInput(val) {
      // Format number with commas, but keep decimals if present
      if (val === null || val === undefined || val === '') return '';
      let str = String(val).replace(/[^0-9.]/g, '');
      if (str === '') return '';
      let parts = str.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    },
    onBalanceInput(e) {
      // Remove commas and update model
      let raw = e.target.value.replace(/,/g, '');
      this.balance = raw;
      this.recalculate();
    },
    onMonthlyPaymentInput(e) {
      let raw = e.target.value.replace(/,/g, '');
      this.monthlyPayment = raw;
      this.recalculate();
    },
    recalculate() {
      // Flip announceToggle to retrigger aria-live for SR users
      this.announceToggle = !this.announceToggle;
    },
    onInput() {
      this.recalculate();
    }
  }
}
</script>
